import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Slider,
  Tooltip,
  IconButton,
  Stack,
  Divider
} from '@mui/material';
import { ShoppingCart, Info, AutoAwesome, TrendingUp } from '@mui/icons-material';
import { PricingTier, calculatePriceForQuantity } from '../utils/api';

interface PricingSelectorProps {
  onPurchase: (quantity: number) => Promise<void>;
  currentCredits?: number;
  isLoading?: boolean;
  recommendedQuantity?: number;
  pricingTiers: PricingTier[];
  bookTitle?: string;
}

const PricingSelector: React.FC<PricingSelectorProps> = ({
  onPurchase,
  currentCredits = 0,
  isLoading = false,
  recommendedQuantity = 10,
  pricingTiers,
  bookTitle
}) => {
  const [quantity, setQuantity] = useState<number>(recommendedQuantity);
  const [inputValue, setInputValue] = useState<string>(recommendedQuantity.toString());
  const [error, setError] = useState<string>('');

  // Update quantity when recommended quantity changes
  useEffect(() => {
    setQuantity(recommendedQuantity);
    setInputValue(recommendedQuantity.toString());
  }, [recommendedQuantity]);

  const handlePurchase = async () => {
    setError('');
    
    try {
      if (quantity < 10) {
        setError('Minimum 10 images');
        return;
      }
      await onPurchase(quantity);
    } catch (err) {
      setError('Failed to process purchase. Please try again.');
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 10) {
      setQuantity(newQuantity);
      setInputValue(newQuantity.toString());
    }
  };

  // Calculate "just enough" credits needed
  const calculateJustEnough = () => {
    const needed = Math.max(10, recommendedQuantity - currentCredits);
    return needed;
  };

  const handleUseRecommended = () => {
    const newQuantity = Math.max(10, recommendedQuantity);
    setQuantity(newQuantity);
    setInputValue(newQuantity.toString());
  };

  const handleJustEnough = () => {
    const justEnoughQuantity = calculateJustEnough();
    setQuantity(justEnoughQuantity);
    setInputValue(justEnoughQuantity.toString());
  };

  // Calculate current pricing
  const currentPricing = calculatePriceForQuantity(quantity, pricingTiers);
  const isValidQuantity = quantity >= 10 && currentPricing !== null;
  const justEnoughQuantity = calculateJustEnough();

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
      {/* Current Balance - Top Section */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
          Current Balance: <strong style={{ color: 'text.primary' }}>{currentCredits} credits</strong>
        </Typography>
      </Box>

      {/* Main Purchase Card */}
      <Card sx={{ 
        backgroundColor: 'background.paper', 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
      }}>
        <CardContent sx={{ p: 4 }}>
          
          {/* Recommended Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
              Recommended for {bookTitle ? bookTitle : 'This Book'}:
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.light', mb: 2 }}>
              {recommendedQuantity} Credits
            </Typography>
          </Box>

          <Divider sx={{ mb: 4, borderColor: 'divider' }} />

          {/* Quick Action Buttons */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Quick Actions
            </Typography>
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
              <Tooltip
                title="Set quantity to the recommended number of images for this book"
                arrow
                placement="top"
              >
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  onClick={handleUseRecommended}
                  sx={{
                    borderColor: 'primary.light',
                    color: 'primary.light',
                    backgroundColor: 'action.hover',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(96, 165, 250, 0.2)',
                      color: 'primary.main'
                    },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Recommended ({recommendedQuantity})
                </Button>
              </Tooltip>
              {currentCredits < recommendedQuantity && (
                <Tooltip
                  title={`Buy exactly ${justEnoughQuantity} credits to reach the recommended ${recommendedQuantity} images (you have ${currentCredits} credits)`}
                  arrow
                  placement="top"
                >
                  <Button
                    variant="outlined"
                    startIcon={<TrendingUp />}
                    onClick={handleJustEnough}
                    sx={{
                      borderColor: 'success.main',
                      color: 'success.main',
                      backgroundColor: 'rgba(52, 211, 153, 0.1)',
                      '&:hover': {
                        borderColor: '#10b981',
                        backgroundColor: 'rgba(52, 211, 153, 0.2)',
                        color: '#10b981'
                      },
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Just Enough ({justEnoughQuantity})
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Box>

          <Divider sx={{ mb: 4, borderColor: 'divider' }} />

          {/* Quantity and Price Section */}
          <Box sx={{ mb: 4 }}>
            {/* Quantity Input and Slider */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'text.secondary' }}>
                Adjust Quantity (Minimum 10 images)
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <TextField
                  type="number"
                  value={inputValue}
                  onChange={(e) => {
                    const newInputValue = e.target.value;
                    setInputValue(newInputValue);
                    
                    const value = parseInt(newInputValue);
                    if (!isNaN(value) && value >= 10 && value <= 300) {
                      setQuantity(value);
                    }
                  }}
                  onBlur={() => {
                    const value = parseInt(inputValue);
                    if (isNaN(value) || value < 10) {
                      setInputValue('10');
                      setQuantity(10);
                    } else if (value > 300) {
                      setInputValue('300');
                      setQuantity(300);
                    }
                  }}
                  inputProps={{ min: 10, style: { textAlign: 'center' } }}
                  sx={{ 
                    width: '100px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'action.hover',
                      borderColor: 'action.disabled',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.light'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.light'
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: 'text.primary'
                    }
                  }}
                  size="small"
                />
              </Box>

              <Slider
                value={quantity}
                onChange={(_, value) => handleQuantityChange(value as number)}
                min={10}
                max={300}
                step={1}
                marks={[
                  { value: 10, label: '10' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: 200, label: '200' },
                  { value: 300, label: '300+' }
                ]}
                sx={{ 
                  mb: 2,
                  '& .MuiSlider-track': {
                    backgroundColor: 'primary.light'
                  },
                  '& .MuiSlider-thumb': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.main'
                    }
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: 'action.disabled'
                  },
                  '& .MuiSlider-markLabel': {
                    color: 'text.secondary'
                  }
                }}
              />
            </Box>

            {/* Price Display */}
            {currentPricing && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                  ${currentPricing.totalPrice.toFixed(2)}
                </Typography>
                
                {/* Savings Display */}
                {isValidQuantity && quantity > 10 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 600 }}>
                      You save: ${((0.20 - currentPricing.pricePerImage) * quantity).toFixed(2)} with volume pricing
                    </Typography>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Pricing Breakdown:
                          </Typography>
                          {quantity >= 10 && (
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              • First 10 images: $0.20 each
                            </Typography>
                          )}
                          {quantity > 10 && (
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              • Next {Math.min(quantity - 10, 39)} images: $0.18 each
                            </Typography>
                          )}
                          {quantity > 49 && (
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              • Next {Math.min(quantity - 49, 50)} images: $0.16 each
                            </Typography>
                          )}
                          {quantity > 99 && (
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              • Remaining {quantity - 99} images: $0.15 each
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ color: 'text.secondary' }}>
                        <Info fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Purchase Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handlePurchase}
            disabled={isLoading || !isValidQuantity}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
              '&:disabled': { backgroundColor: 'action.disabled' },
              textTransform: 'none',
              fontWeight: 600,
              py: 2,
              fontSize: '1.1rem',
              borderRadius: 2
            }}
            startIcon={isLoading ? <CircularProgress size={20} /> : <ShoppingCart />}
          >
            {isLoading ? 'Processing...' : `Purchase ${quantity} Credits`}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PricingSelector;